// @ts-strict-ignore
import * as React from 'react';
import * as API from './../../api';
import Modal from 'antd/lib/modal/Modal';
import Form from 'antd/lib/form';
import Checkbox from 'antd/lib/checkbox';
import {
    defaultBuildFileTypesByAppStoreType,
    getIsApplePlatform,
    getIsOTAPlatform,
    getSeparatedWorkflowFields,
    isGooglePlatform,
    isIntegrationBuildDistroSupported,
} from '../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectBuildSystemIntegrations,
    selectVCSIntegration,
    selectProviderForIntegration,
    selectAuthCredentials,
    selectAppName,
    selectWorkflowEnabledSteps,
    selectDestinationInfoByDestinationStepConfigId,
    selectStepGraph,
    selectOrg,
} from '../../selectors/selectors';
import { BASE_BRANCH_FIELD_RULES, FieldType, FormFieldData, renderFormFields } from '../../helpers/formHelpers';
import Divider from 'antd/lib/divider';
import Switch from 'antd/lib/switch';
import {
    tryNewBuildDistributionBucket,
    tryUpdateBuildDistributionBucket,
    TRY_NEW_BUILD_DISTRIBUTION_BUCKET,
} from '../../actions/buildDistribution';
import { AddEditBucketMembers } from './AddEditBucketMembers';
import { globalMessage } from '../../utils/globalMessage';
import Select from 'antd/lib/select';
import {
    BuildDistroBucketBase,
    BucketRule,
    BuildDistroBucketRuleType,
    IntegrationType,
    PatternToken,
    User,
    WorkflowArgument,
    VCSUserData,
    BuildDistroFilterByVCSUsersBehaviorType,
    BuildDistroFilterByPRLabelsBehaviorType,
} from '../../types';
import { InfoCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Popover from 'antd/lib/popover';
import { PatternTokenHelperTextComponent } from '../appSettings/PatternTokenHelperTextComponent';
import { CreateBuildDistributionBucketRequest } from '../../actions/entities';
import { BuildDistributionUpdatable } from '../../actions/updatable';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import { displayNameForBuildDistroBucketRuleType, displayUserNameShort } from '../../helpers/displayHelpers';
import { NamePath } from 'antd/lib/form/interface';
import { displayNameForIntegrationType, getArgumentsRecordFromArray } from '../../helpers/integrationHelpers';
import Space from 'antd/lib/space';
import IntegrationIcon from '../icons/IntegrationIcon';
import { workflowArgumentField } from '../../helpers/integrationFormHelpers';
import { capitalize, uniqBy } from 'lodash';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { destinationsByIntegrationInstallationId } from '../Integrations';

const AVAILABLE_FILENAME_TOKENS: PatternToken[] = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.ReleaseName,
    PatternToken.Wildcard,
];

const AVAILABLE_BRANCH_TOKENS: PatternToken[] = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.ReleaseName,
    PatternToken.Wildcard,
];

const parseWorkflowIdFields = (workflowValue: string): { workflowId: string; appSlug: string | null } => {
    const parts = workflowValue.split(',');
    return {
        appSlug: parts.length > 1 ? parts[0]?.trim() : null,
        workflowId: parts[parts.length - 1]?.trim() ?? workflowValue,
    };
};

const workflowFieldsFromFormValue = (
    workflowData,
    integrationType?: IntegrationType,
): { workflowId: string; appSlug: string | null } => {
    if (Array.isArray(workflowData)) {
        return {
            appSlug: workflowData[0],
            workflowId: workflowData[1],
        };
    }

    if (integrationType === IntegrationType.AzureCI && typeof workflowData === 'string') {
        return parseWorkflowIdFields(workflowData);
    }

    return {
        appSlug: null,
        workflowId: workflowData,
    };
};

const appSlugForWorkflow = (
    appSlugFromForm: string | null,
    workflowId: string,
    existingWorkflowData?: BucketRule['workflowData'],
): string | null => {
    if (appSlugFromForm) {
        return appSlugFromForm;
    }

    if (existingWorkflowData?.workflowId === workflowId || existingWorkflowData?.workflowName === workflowId) {
        return existingWorkflowData.appSlug ?? null;
    }

    return null;
};

const workflowFormValueFromRule = (rule: BucketRule, hasWorkflowPicker: boolean, integrationType?: IntegrationType) => {
    if (!rule.workflowData) {
        return undefined;
    }

    if (rule.workflowData.appSlug) {
        if (!hasWorkflowPicker && integrationType === IntegrationType.AzureCI) {
            return `${rule.workflowData.appSlug},${rule.workflowData.workflowId}`;
        }

        return [rule.workflowData.appSlug, rule.workflowData.workflowId];
    }

    if (!hasWorkflowPicker) {
        return rule.workflowData.workflowName || rule.workflowData.workflowId;
    }

    return rule.workflowData.workflowId;
};

export interface CreateEditBuildBucketModalProps {
    bucket?: BuildDistroBucketBase;
    open: boolean;
    onCancel: () => void;
    appId: string;
    orgId: string;
}

export interface RunwayOrVCSUser {
    vcsUser: VCSUserData;
    user?: User;
}

const RunwayOrVCSUserLabel: React.FC<{ runwayOrVCSUser: RunwayOrVCSUser; vcsIntegrationId: IntegrationType }> = ({
    runwayOrVCSUser: { vcsUser, user },
    vcsIntegrationId,
}) => {
    const name = user && displayUserNameShort(user);

    const vcsIcon = vcsIntegrationId && <IntegrationIcon size={20} integrationId={vcsIntegrationId} />;

    return (
        <Space align="center">
            {vcsIcon}
            <Text>{vcsUser.username}</Text>
            {name && <Text type="secondary">{`(${name})`}</Text>}
        </Space>
    );
};

export const CreateEditBuildBucketModal: React.FC<CreateEditBuildBucketModalProps> = ({
    appId,
    orgId,
    open,
    onCancel,
    bucket,
}) => {
    const [form] = Form.useForm();

    const isEditing = !!bucket;

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const appName = useSelector((state: RootState) => selectAppName(state, appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const buildSystemIntegrations = useSelector((state: RootState) => selectBuildSystemIntegrations(state, appId));
    const vcsSystemIntegration = useSelector((state: RootState) => selectVCSIntegration(state, appId));
    const selectedBuildSystemIntegrationId = Form.useWatch('buildSystemIntegrationInstallationId', form);
    const defaultBuildSystemIntegrationId = buildSystemIntegrations[0]?.installationId;
    const effectiveBuildSystemIntegrationId =
        selectedBuildSystemIntegrationId ??
        bucket?.buildSystemIntegrationInstallationId ??
        defaultBuildSystemIntegrationId;
    const buildSystemIntegration = buildSystemIntegrations.find(
        (integration) => integration.installationId === effectiveBuildSystemIntegrationId,
    );
    const integrationProvider = useSelector((state: RootState) =>
        buildSystemIntegration ? selectProviderForIntegration(state, orgId, buildSystemIntegration) : null,
    );
    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const stepGraph = useSelector((state: RootState) => selectStepGraph(state, appId));
    const orgTags = useSelector((state: RootState) => selectOrg(state)?.tags ?? []);
    const destinationsByIntegrationId = React.useMemo(
        () => destinationsByIntegrationInstallationId(stepGraph, destinationInfoByDestinationStepConfigId, orgTags),
        [stepGraph, destinationInfoByDestinationStepConfigId, orgTags],
    );
    const requiresSupportedBuildSystemIntegration =
        buildSystemIntegration ? !isIntegrationBuildDistroSupported(buildSystemIntegration.id) : true;
    const buildSystemIntegrationOptions = buildSystemIntegrations.map((integration) => {
        const alias =
            typeof integration.alias === 'string' && integration.alias.trim().length > 0 ?
                integration.alias.trim()
            :   null;
        const destinations = destinationsByIntegrationId[integration.installationId] ?? [];

        return {
            value: integration.installationId,
            searchText: [integration.name, alias, displayNameForIntegrationType(integration.id)]
                .filter(Boolean)
                .join(' '),
            label: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <Space size={4}>
                        <Text>{integration.name}</Text>
                        {alias && <Text type="secondary">{`(${alias})`}</Text>}
                    </Space>
                    <DestinationCounter countOnly size={20} destinations={destinations} />
                </div>
            ),
        };
    });

    const hasVCSIntegration = !!vcsSystemIntegration;

    // CI integration not required in form if app has no integrations
    const hasBuildSystemIntegrations = buildSystemIntegrationOptions.length > 0;

    // TODO - Bitrise pipelines support for BD
    //const selectedBitriseIsPipelineDev = useWatch(['bitriseIsPipelineDev'], form);

    const { workingBranchWorkflowFields } = getSeparatedWorkflowFields(
        workflowEnabledSteps,
        buildSystemIntegration,
        platform,
        appStoreType,
        appId,
        appName,
        true,
        integrationProvider,
    );

    const dispatch = useDispatch();

    const isCreatingNewBuildDistributionBucket = useIsLoading(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);
    const buildDistributionBucketError = useError(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);
    const createBuildDistributionBucketSuccess = useUpdated(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);

    const isUpdatingBuildDistributionBucket = useIsLoading(
        BuildDistributionUpdatable.UpdateBuildDistroBucket,
        bucket?.id,
    );
    const updateBuildDistributionBucketError = useError(BuildDistributionUpdatable.UpdateBuildDistroBucket, bucket?.id);
    const updateBuildDistributionBucketSuccess = useUpdated(
        BuildDistributionUpdatable.UpdateBuildDistroBucket,
        bucket?.id,
    );

    React.useEffect(() => {
        if (createBuildDistributionBucketSuccess) {
            globalMessage.success('Bucket created successfully!');
        }
        if (updateBuildDistributionBucketSuccess) {
            globalMessage.success('Bucket updated successfully!');
        }

        onCancel();
    }, [createBuildDistributionBucketSuccess, updateBuildDistributionBucketSuccess]);

    React.useEffect(() => {
        if (buildDistributionBucketError) {
            globalMessage.error(buildDistributionBucketError);
        }

        if (updateBuildDistributionBucketError) {
            globalMessage.error(updateBuildDistributionBucketError);
        }
    }, [buildDistributionBucketError, updateBuildDistributionBucketError]);

    React.useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, bucket?.id, form]);

    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, appId));

    const [repoContributors, setRepoContributors] = React.useState<VCSUserData[]>([]);

    const appUsersWithVCSUsers: RunwayOrVCSUser[] =
        appUsers
            ?.filter((user) => user.vcsUserDataByProvider?.[vcsSystemIntegration?.providerId])
            .map((user) => ({
                vcsUser: user.vcsUserDataByProvider[vcsSystemIntegration?.providerId],
                user,
            })) ?? [];

    const allRunwayOrVCSUsers: RunwayOrVCSUser[] = uniqBy(
        [...appUsersWithVCSUsers, ...repoContributors.map((contributor) => ({ vcsUser: contributor }))],
        (runwayOrVCSUser) => runwayOrVCSUser.vcsUser.username,
    );

    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));

    React.useEffect(() => {
        if (open) {
            API.getAppRepoContributors(
                {
                    appId: appId,
                    orgId: orgId,
                    bucketId: bucket?.id,
                },
                authCreds,
            )
                .then((response) => {
                    setRepoContributors(response.contributors ?? []);
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [open, authCreds]);

    const createEditBucket = (values) => {
        const requestData = {
            ...values,
            appId,
            orgId,
            buildSystemIntegrationInstallationId: values.buildSystemIntegrationInstallationId,
            rules: values?.rules?.map((rule, index) => {
                const workflowArguments = getArgumentsRecordFromArray(rule.workflowArguments);
                const workflowData = rule.workflowData;
                const existingWorkflowData = bucket?.rules?.[index]?.workflowData;
                const filterByVCSUsers = allRunwayOrVCSUsers
                    .filter((user) => {
                        return rule.filterByVCSUsersUsernames?.includes(user.vcsUser.username);
                    })
                    .map((user) => user.vcsUser);
                if (workflowData) {
                    const { workflowId, appSlug } = workflowFieldsFromFormValue(
                        workflowData,
                        buildSystemIntegration?.id,
                    );

                    let subWorkflowId = rule.subWorkflowId;
                    if (subWorkflowId == '') {
                        subWorkflowId = null;
                    }

                    return {
                        ...rule,
                        workflowData: {
                            ...rule.workflowData,
                            workflowId: workflowId,
                            subWorkflowId: subWorkflowId,
                            appSlug: appSlugForWorkflow(appSlug, workflowId, existingWorkflowData),
                            variables: workflowArguments,
                        },
                        filterByVCSUsers: filterByVCSUsers,
                        filterByVCSUsersBehavior: rule.filterByVCSUsersBehavior,
                    };
                }

                return {
                    ...rule,
                    filterByVCSUsers: filterByVCSUsers,
                    filterByVCSUsersBehavior: rule.filterByVCSUsersBehavior,
                };
            }),
        };

        dispatch(
            isEditing ?
                tryUpdateBuildDistributionBucket(BuildDistributionUpdatable.UpdateBuildDistroBucket, {
                    bucketId: bucket.id,
                    ...requestData,
                })
            :   tryNewBuildDistributionBucket({
                    ...requestData,
                } as CreateBuildDistributionBucketRequest),
        );
    };

    const name = Form.useWatch('name', form);
    const rules = Form.useWatch('rules', form);

    const orgWideAccessEnabled = Form.useWatch('orgWideAccessEnabled', form);
    const publicBucketLinkEnabled = Form.useWatch('publicBucketLinkEnabled', form);

    const defaultRule: BucketRule = {
        type: BuildDistroBucketRuleType.Branch,
        fileFilterPatterns: [],
        branch: null,
        filterByVCSUsers: [],
        pullInAdditionalArtifacts: false,
        filterByVCSUsersBehavior: BuildDistroFilterByVCSUsersBehaviorType.Include,
        filterByPRLabels: [],
        filterByPRLabelsBehavior: BuildDistroFilterByPRLabelsBehaviorType.AnyOf,
    };

    const isIos = getIsApplePlatform(platform);
    const isAndroid = isGooglePlatform(platform);
    const isOTA = getIsOTAPlatform(platform);

    const hasReachedMaxRulesLimit = rules?.length >= 1;

    return (
        <Modal
            width={600}
            open={open}
            title={isEditing ? `Edit bucket` : 'Create bucket'}
            okText={isEditing ? 'Save' : `Create ${name ? `'${name}' bucket` : 'bucket'}`}
            onOk={() => {
                form.validateFields()
                    .then(() => {
                        form.submit();
                    })
                    .catch(() => null);
            }}
            okButtonProps={{
                loading: isCreatingNewBuildDistributionBucket || isUpdatingBuildDistributionBucket,
                disabled: !name || (hasBuildSystemIntegrations && !effectiveBuildSystemIntegrationId),
            }}
            onCancel={onCancel}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={
                    isEditing ?
                        {
                            name: bucket?.name,
                            buildSystemIntegrationInstallationId:
                                bucket?.buildSystemIntegrationInstallationId ?? defaultBuildSystemIntegrationId,
                            orgWideAccessEnabled: bucket?.orgWideAccessEnabled,
                            publicBucketLinkEnabled: bucket?.publicBucketLinkEnabled,
                            rules: bucket?.rules.map((rule) => {
                                const workflowArguments: Record<string, string> = rule.workflowData?.variables;
                                const argumentsForWorkflow: WorkflowArgument[] = [];
                                if (workflowArguments) {
                                    for (const [key, value] of Object.entries(workflowArguments)) {
                                        argumentsForWorkflow.push({
                                            argumentKey: key,
                                            argumentValue: value,
                                        });
                                    }
                                }
                                return {
                                    ...rule,
                                    workflowData: workflowFormValueFromRule(
                                        rule,
                                        workingBranchWorkflowFields.length > 0,
                                        buildSystemIntegration?.id,
                                    ),
                                    subWorkflowId: rule.workflowData?.subWorkflowId,
                                    workflowArguments: argumentsForWorkflow,
                                    filterByVCSUsersUsernames:
                                        rule.filterByVCSUsers?.map((user) => user.username) ?? [],
                                    filterByVCSUsersBehavior:
                                        rule.filterByVCSUsersBehavior ??
                                        BuildDistroFilterByVCSUsersBehaviorType.Include,
                                    filterByPRLabels: rule.filterByPRLabels ?? [],
                                    filterByPRLabelsBehavior:
                                        rule.filterByPRLabelsBehavior ?? BuildDistroFilterByPRLabelsBehaviorType.AnyOf,
                                };
                            }),
                            members: bucket?.members,
                        }
                    :   {
                            name: 'New bucket',
                            buildSystemIntegrationInstallationId: defaultBuildSystemIntegrationId,
                            orgWideAccessEnabled: false,
                            publicBucketLinkEnabled: false,
                            members: [],
                            rules: [],
                        }
                }
                onFinish={createEditBucket}
                preserve={false}
            >
                <Form.Item
                    label="Bucket name"
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: 'Please enter a bucket name',
                        },
                    ]}
                >
                    <Input placeholder="Enter a bucket name" />
                </Form.Item>
                {isEditing && (
                    <Space style={{ marginTop: 4, marginBottom: 24 }} align="center">
                        <Text type="secondary">Bucket identifier: </Text>
                        <Text
                            style={{
                                backgroundColor: antGrey.grey5,
                                color: runwayColors.darkTextGrey,
                                borderRadius: 3,
                                borderStyle: 'solid',
                                borderColor: antGrey.grey7,
                                borderWidth: 1,
                                padding: '2px 4px',
                            }}
                        >
                            {bucket.id}
                        </Text>
                    </Space>
                )}
                <Form.Item
                    label="CI integration"
                    name="buildSystemIntegrationInstallationId"
                    rules={[
                        {
                            required: hasBuildSystemIntegrations,
                            message: 'Please select a CI integration',
                        },
                    ]}
                >
                    <Select
                        showSearch
                        placeholder={
                            hasBuildSystemIntegrations ? 'Select a CI integration' : 'No CI integrations connected'
                        }
                        options={buildSystemIntegrationOptions}
                        filterOption={(input, option) =>
                            `${option?.searchText ?? ''}`.toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={!hasBuildSystemIntegrations}
                    />
                </Form.Item>
                <Divider />
                <Form.Item label="Bucket rules">
                    <Text type="secondary">
                        Configure rules that determine which builds automatically get pulled into this bucket from CI
                        workflows.
                    </Text>
                </Form.Item>
                {requiresSupportedBuildSystemIntegration && (
                    <Text type="warning">
                        Connect a supported build system integration to add a rule for this bucket
                    </Text>
                )}
                <Form.List
                    name="rules"
                    rules={[
                        {
                            validator: async (_, rules) => {
                                if (
                                    !hasVCSIntegration &&
                                    rules.some((rule) => rule.type === BuildDistroBucketRuleType.PR)
                                ) {
                                    return Promise.reject(
                                        new Error('Cannot add pull request rules without a VCS integration'),
                                    );
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => {
                        return (
                            <>
                                <Form.Item
                                    shouldUpdate
                                    noStyle
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                        marginTop: 12,
                                    }}
                                >
                                    {() =>
                                        fields.map((field, index) => {
                                            const workflowFields: FormFieldData[] =
                                                workingBranchWorkflowFields.length === 0 ?
                                                    [
                                                        {
                                                            fieldType: FieldType.Input,
                                                            disabled: requiresSupportedBuildSystemIntegration,
                                                            placeholder: 'build',
                                                        },
                                                    ]
                                                :   workingBranchWorkflowFields;

                                            const mappedFields = workflowFields.map((f, i) => {
                                                // a bit of a hacky way to get the right label given integrations call workflows different things, and to account for sub workflow (Jenkins)
                                                const label =
                                                    f.label ?
                                                        capitalize((f.label as string).split(' ').pop())
                                                    :   'Workflow';

                                                // since we take working branch fields, all are optional, so ensure first is required
                                                const rules = f.rules ?? [];
                                                if (i == 0) {
                                                    rules.push({
                                                        required: true,
                                                    });
                                                }

                                                // if i > 0, Jenkins sub workflow case
                                                const namePath = i == 0 ? 'workflowData' : 'subWorkflowId';

                                                return {
                                                    ...f,
                                                    helpMessage: null,
                                                    label: label,
                                                    name: [field.name, namePath] as string & NamePath,
                                                    extraData: [
                                                        {
                                                            placeholder: 'Select workflow',
                                                            allowClear: true,
                                                        },
                                                    ],
                                                    rules: rules,
                                                };
                                            });

                                            const ruleType = form.getFieldValue(['rules', field.name, 'type']);
                                            const isBranchRule = ruleType === BuildDistroBucketRuleType.Branch;
                                            const isPRRule = ruleType === BuildDistroBucketRuleType.PR;

                                            const requiresVCSIntegration = !hasVCSIntegration;

                                            const argumentsField =
                                                buildSystemIntegration ?
                                                    shouldShowWorkflowArgumentsField(buildSystemIntegration.id) ?
                                                        workflowArgumentField(null, null, [
                                                            field.name,
                                                            'workflowArguments',
                                                        ] as string & NamePath)
                                                    :   null
                                                :   null;

                                            const workflowBranchFields: FormFieldData[] = [...mappedFields];
                                            if (argumentsField) {
                                                workflowBranchFields.push(argumentsField);
                                            }

                                            workflowBranchFields.push({
                                                label: isBranchRule ? 'Branch' : 'Base branch',
                                                name: [field.name, isBranchRule ? 'branch' : 'baseBranch'] as string &
                                                    NamePath,
                                                fieldType: FieldType.Input,
                                                rules: [
                                                    ...BASE_BRANCH_FIELD_RULES,
                                                    {
                                                        required: true,
                                                    },
                                                ],
                                                disabled: requiresSupportedBuildSystemIntegration,
                                                placeholder: 'main',
                                                helpMessage: (
                                                    <Text type="secondary">
                                                        Tokenized branch patterns are {!isBranchRule && 'not '}{' '}
                                                        supported.{' '}
                                                        {!isBranchRule ? null : (
                                                            <Popover
                                                                overlayInnerStyle={{
                                                                    width: 640,
                                                                    maxHeight: 200,
                                                                    overflowY: 'auto',
                                                                }}
                                                                content={
                                                                    <PatternTokenHelperTextComponent
                                                                        availableTokens={AVAILABLE_BRANCH_TOKENS}
                                                                    />
                                                                }
                                                            >
                                                                <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                            </Popover>
                                                        )}
                                                    </Text>
                                                ),
                                            });

                                            const helperText =
                                                isBranchRule ?
                                                    'Build artifacts will be automatically pulled into the bucket from matching workflow runs on the configured branch.'
                                                :   'Build artifacts will be automatically pulled into the bucket from matching workflow runs on any pull requests that are open against the base branch.';
                                            const userHelpText =
                                                isBranchRule ?
                                                    `Filter by commit author – only builds triggered by commits authored by one of the specified users will be pulled into the bucket.`
                                                :   vcsSystemIntegration &&
                                                    `Filter ${
                                                        isBranchRule ? 'branches' : 'PRs'
                                                    } to those created by the specified users. Select one or more users or enter a ${displayNameForIntegrationType(
                                                        vcsSystemIntegration.id,
                                                    )} username.`;

                                            return (
                                                <div
                                                    key={field.key}
                                                    style={{
                                                        width: '100%',
                                                        backgroundColor: antGrey.grey2,
                                                        borderRadius: 8,
                                                        padding: 20,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            display: 'flex',
                                                            gap: 12,
                                                            alignItems: 'start',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: 12,
                                                                backgroundColor: runwayColors.primary,
                                                                color: 'white',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                margin: '0 6px',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </div>
                                                        <div>
                                                            <Form.Item
                                                                label="Rule type"
                                                                name={[field.name, 'type']}
                                                                help={helperText}
                                                            >
                                                                <Select
                                                                    style={{ width: '100%' }}
                                                                    value={form.getFieldValue([
                                                                        'rules',
                                                                        field.name,
                                                                        'type',
                                                                    ])} // Explicitly get value (needed due to Form.ErrorList as part of this Form.Item)
                                                                    onChange={(value) =>
                                                                        form.setFieldValue(
                                                                            ['rules', field.name, 'type'],
                                                                            value,
                                                                        )
                                                                    } // Update form manually (needed due to Form.ErrorList as part of this Form.Item)
                                                                    options={Object.values(
                                                                        BuildDistroBucketRuleType,
                                                                    ).map((type) => {
                                                                        return {
                                                                            label: displayNameForBuildDistroBucketRuleType(
                                                                                type,
                                                                            ),
                                                                            value: type,
                                                                        };
                                                                    })}
                                                                />
                                                                <Form.ErrorList errors={errors} />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="CI/CD workflow and branch"
                                                                style={{
                                                                    marginBottom: 0,
                                                                }}
                                                            >
                                                                {renderFormFields(workflowBranchFields)}
                                                            </Form.Item>
                                                            {isPRRule && (
                                                                <div style={{ display: 'flex', gap: '16px' }}>
                                                                    <Form.Item
                                                                        label="PR labels"
                                                                        name={[field.name, 'filterByPRLabels']}
                                                                        style={{
                                                                            paddingBottom: 24,
                                                                            width: '75%',
                                                                        }}
                                                                        help="Filter PRs by label. Use newlines to enter multiple labels."
                                                                    >
                                                                        <Select
                                                                            mode="tags"
                                                                            allowClear
                                                                            tokenSeparators={['\n']}
                                                                            dropdownStyle={{ display: 'none' }}
                                                                            style={{ width: '100%' }}
                                                                        />
                                                                    </Form.Item>
                                                                    <Form.Item
                                                                        name={[field.name, 'filterByPRLabelsBehavior']}
                                                                        style={{
                                                                            paddingBottom: 24,
                                                                            width: '25%',
                                                                        }}
                                                                    >
                                                                        <Select
                                                                            style={{ marginTop: 30 }}
                                                                            options={[
                                                                                {
                                                                                    label: 'any of',
                                                                                    value: BuildDistroFilterByPRLabelsBehaviorType.AnyOf,
                                                                                },
                                                                                {
                                                                                    label: 'none of',
                                                                                    value: BuildDistroFilterByPRLabelsBehaviorType.NoneOf,
                                                                                },
                                                                                {
                                                                                    label: 'all of',
                                                                                    value: BuildDistroFilterByPRLabelsBehaviorType.AllOf,
                                                                                },
                                                                            ]}
                                                                        />
                                                                    </Form.Item>
                                                                </div>
                                                            )}
                                                            <div style={{ display: 'flex', gap: '16px' }}>
                                                                <Form.Item
                                                                    label={`Users`}
                                                                    name={[field.name, 'filterByVCSUsersUsernames']}
                                                                    style={{
                                                                        paddingBottom: 24,
                                                                        width: '75%',
                                                                    }}
                                                                    help={userHelpText}
                                                                >
                                                                    <Select
                                                                        mode="tags"
                                                                        allowClear
                                                                        disabled={requiresVCSIntegration}
                                                                        options={allRunwayOrVCSUsers.map(
                                                                            (runwayOrVCSUser) => {
                                                                                return {
                                                                                    label: (
                                                                                        <RunwayOrVCSUserLabel
                                                                                            key={
                                                                                                runwayOrVCSUser.vcsUser
                                                                                                    .username
                                                                                            }
                                                                                            runwayOrVCSUser={
                                                                                                runwayOrVCSUser
                                                                                            }
                                                                                            vcsIntegrationId={
                                                                                                vcsSystemIntegration.id
                                                                                            }
                                                                                        />
                                                                                    ),
                                                                                    value: runwayOrVCSUser.vcsUser
                                                                                        .username,
                                                                                };
                                                                            },
                                                                        )}
                                                                    />
                                                                </Form.Item>
                                                                <Form.Item
                                                                    name={[field.name, 'filterByVCSUsersBehavior']}
                                                                    style={{
                                                                        paddingBottom: 24,
                                                                        width: '25%',
                                                                    }}
                                                                >
                                                                    <Select
                                                                        allowClear
                                                                        disabled={requiresVCSIntegration}
                                                                        style={{ marginTop: 30 }}
                                                                        options={[
                                                                            {
                                                                                label: 'any of',
                                                                                value: BuildDistroFilterByVCSUsersBehaviorType.Include,
                                                                            },
                                                                            {
                                                                                label: 'none of',
                                                                                value: BuildDistroFilterByVCSUsersBehaviorType.Exclude,
                                                                            },
                                                                        ]}
                                                                    />
                                                                </Form.Item>
                                                            </div>
                                                            <Form.Item
                                                                label="File name(s)"
                                                                extra={
                                                                    <Text type="secondary">
                                                                        The file names of the artifact files that will
                                                                        be surfaced for each build. Unless unset by the
                                                                        user, the default configuration is that all{' '}
                                                                        {defaultBuildFileTypesByAppStoreType(
                                                                            appStoreType,
                                                                        )}{' '}
                                                                        files will be surfaced. Tokenized patterns and
                                                                        wildcards are supported.{' '}
                                                                        <Popover
                                                                            overlayInnerStyle={{
                                                                                width: 640,
                                                                                maxHeight: 200,
                                                                                overflowY: 'auto',
                                                                            }}
                                                                            content={
                                                                                <PatternTokenHelperTextComponent
                                                                                    availableTokens={
                                                                                        AVAILABLE_FILENAME_TOKENS
                                                                                    }
                                                                                />
                                                                            }
                                                                        >
                                                                            <InfoCircleOutlined
                                                                                style={{ color: antGrey.grey7 }}
                                                                            />
                                                                        </Popover>
                                                                    </Text>
                                                                }
                                                                style={{
                                                                    width: '100%',
                                                                }}
                                                                rules={[
                                                                    {
                                                                        validator: async (_, filenames) => {
                                                                            if (Array.isArray(filenames)) {
                                                                                for (const filename of filenames) {
                                                                                    if (
                                                                                        isOTA ||
                                                                                        !(isIos || isAndroid)
                                                                                    ) {
                                                                                        continue; // only validate for google or android platforms, skip otherwise
                                                                                    }

                                                                                    const isValidFilename =
                                                                                        isIos ?
                                                                                            filename.endsWith('.ipa')
                                                                                        :   filename.endsWith('.apk') ||
                                                                                            filename.endsWith('.aab');

                                                                                    if (!isValidFilename) {
                                                                                        return Promise.reject(
                                                                                            isIos ?
                                                                                                'Artifact filenames must be of type .ipa'
                                                                                            :   'Artifact filenames must be of type .apk or .aab',
                                                                                        );
                                                                                    }
                                                                                }
                                                                            }
                                                                            return Promise.resolve();
                                                                        },
                                                                    },
                                                                ]}
                                                                name={[field.name, 'fileFilterPatterns']}
                                                            >
                                                                <Select
                                                                    mode="tags"
                                                                    allowClear
                                                                    tokenSeparators={[',', '\n']}
                                                                    dropdownStyle={{ display: 'none' }}
                                                                    style={{ width: '100%' }}
                                                                    placeholder={isIos ? 'app.ipa' : 'app.apk'}
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                name={[field.name, 'pullInAdditionalArtifacts']}
                                                                valuePropName="checked"
                                                                help="All build artifacts produced by the configured workflow will be pulled in alongside each binary and surfaced as additional artifacts"
                                                            >
                                                                <Checkbox>Include additional build artifacts</Checkbox>
                                                            </Form.Item>
                                                        </div>
                                                        <Button
                                                            icon={
                                                                <DeleteOutlined
                                                                    style={{ color: runwayColors.statusRed }}
                                                                />
                                                            }
                                                            danger
                                                            type="link"
                                                            onClick={() => remove(index)}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                </Form.Item>
                                {!hasReachedMaxRulesLimit && (
                                    <Button
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        style={{ marginTop: 12 }}
                                        disabled={requiresSupportedBuildSystemIntegration}
                                        onClick={() => {
                                            add({
                                                ...defaultRule,
                                            });
                                        }}
                                    >
                                        Add a rule
                                    </Button>
                                )}
                            </>
                        );
                    }}
                </Form.List>

                <Divider />
                <Form.Item
                    label="Org-wide access"
                    name="orgWideAccessEnabled"
                    valuePropName="checked"
                    extra={
                        orgWideAccessEnabled ?
                            'Anyone in your Runway org with the bucket link can view and install.'
                        :   'Only bucket members can view and install.'
                    }
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    label="Enable public bucket link"
                    name="publicBucketLinkEnabled"
                    valuePropName="checked"
                    extra={
                        publicBucketLinkEnabled ?
                            <Text style={{ color: runwayColors.statusYellow }}>
                                Anyone with the public bucket link will be able to view a public version of the bucket
                                and install bucket builds on their device. The link can also be shared with others.
                            </Text>
                        :   'Only org members with bucket access will be able to view the bucket and install bucket builds on their device.'
                    }
                >
                    <Switch />
                </Form.Item>
                {!isEditing && (
                    <>
                        <Form.Item hidden name="members" />
                        <Divider />
                        <AddEditBucketMembers
                            initialMembers={[]}
                            excludedMembers={[]}
                            appId={appId}
                            orgId={orgId}
                            onMembersChange={(members = []) => {
                                form.setFieldsValue({ members } as CreateBuildDistributionBucketRequest);
                            }}
                        />
                    </>
                )}
            </Form>
        </Modal>
    );
};

const shouldShowWorkflowArgumentsField = (integration: IntegrationType): boolean => {
    if (integration == null) {
        return false;
    }
    switch (integration) {
        case IntegrationType.GitHubCI:
        case IntegrationType.GitLabCI:
        case IntegrationType.Codemagic:
        case IntegrationType.Bitrise:
        case IntegrationType.Buildkite:
        case IntegrationType.Jenkins:
        case IntegrationType.CircleCI:
        case IntegrationType.AzureCI:
            return true;
    }

    return false;
};
